"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.defaultProps = exports.default = exports.Ratings = void 0;
require("./Ratings.css");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _renderObject = require("../../util/renderObject/renderObject");
var _Link = _interopRequireDefault(require("../Link/Link"));
var _functions = require("./utils/functions");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * Rating component
 * @method
 * @param {object} props - React properties passed from composition
 * @returns Ratings
 */
/**
 * Primary UI component for user interaction
 */

const Ratings = props => {
  const {
    variant = defaultProps.variant,
    maxRating = defaultProps.maxRating,
    rating = defaultProps.rating,
    ratingAction,
    reviewsCount,
    qAndAAction,
    writeReviewAction,
    reviewable = defaultProps.reviewable,
    reviewAccessibilityLabel,
    externalReviewLink,
    externalQandALink,
    externalWriteReviewLink
  } = props;
  const stars = (0, _functions.starIcons)({
    rating,
    maxRating
  });
  const hasReviewsAndRating = stars.length > 0 && !!reviewsCount;

  // ratingAction and reviews are needed for review link (PDP)
  const shouldHaveReviewLink = variant === 'PDP' && reviewable && (ratingAction?.label && !!reviewsCount || externalReviewLink);

  // qAndAAction is needed for qAndA link (PDP)
  const shouldHaveQAndALink = variant === 'PDP' && (qAndAAction?.label || externalQandALink);

  // writeReviewAction is needed for write a review link
  const shouldHaveWriteReviewlink = !reviewsCount && variant === 'PDP' && reviewable && (writeReviewAction?.label || externalWriteReviewLink);
  return /*#__PURE__*/_react.default.createElement("div", {
    className: 'pal-c-Ratings'
  }, !!reviewAccessibilityLabel && /*#__PURE__*/_react.default.createElement("span", {
    className: "sr-only"
  }, reviewAccessibilityLabel), hasReviewsAndRating && (0, _functions.composeRating)({
    rating,
    stars,
    variant,
    reviewsCount
  }), shouldHaveReviewLink && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, externalReviewLink && (0, _renderObject.renderObject)(externalReviewLink), !externalReviewLink && /*#__PURE__*/_react.default.createElement("span", {
    className: "pal-c-Ratings__reviewsLink"
  }, /*#__PURE__*/_react.default.createElement(_Link.default, {
    size: "compact",
    iconPosition: "after",
    iconName: "arrowDown",
    href: ratingAction?.url,
    label: ratingAction?.label
  }))), shouldHaveQAndALink && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, externalQandALink && (0, _renderObject.renderObject)(externalQandALink), !externalQandALink && /*#__PURE__*/_react.default.createElement("span", {
    className: "pal-c-Ratings__qAndALink"
  }, /*#__PURE__*/_react.default.createElement(_Link.default, {
    size: "compact",
    iconPosition: "after",
    iconName: "arrowDown",
    href: qAndAAction?.url,
    label: qAndAAction?.label
  }))), shouldHaveWriteReviewlink && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, externalWriteReviewLink && (0, _renderObject.renderObject)(externalWriteReviewLink), !externalWriteReviewLink && /*#__PURE__*/_react.default.createElement("span", {
    className: "pal-c-Ratings__writeReviewLink"
  }, /*#__PURE__*/_react.default.createElement(_Link.default, {
    size: "compact",
    href: writeReviewAction?.url,
    label: writeReviewAction?.label
  }))));
};

/**
 * Property type definitions
 * @type {object}
 * @property {oneOf} variant - sets the variant of the ratings
 * @property {number} maxRating - Maximum number of stars to display
 * @property {number} rating - 0 to 5 decimal number indicating the rating
 * @property {object} ratingAction - holds the action on rating
 * @property {oneOfType} reviewsCount - Number of total reviews
 * @property {object} qAndAAction - holds the action for QandA
 * @property {boolean} reviewable - Flag that indicates if a "write a review" link show display
 * @property {object} writeReviewAction - Text and link to indicate there are no reviews
 * @property {object} externalReviewLink - External review link object
 * @property {object} externalQandALink - External Q and A link object
 * @property {object} externalWriteReviewLink - External write a review link object
 */
exports.Ratings = Ratings;
Ratings.propTypes = {
  /**
   * Sets the variant of the ratings
   */
  variant: _propTypes.default.oneOf(['default', 'PDP']),
  /**
   * Maximum number of stars to display
   */
  maxRating: _propTypes.default.number,
  /**
   * 0 to 5 decimal number indicating the rating
   */
  rating: _propTypes.default.number,
  /**
   * Holds the action on rating
   * - **label:** Label for the action on rating
   * - **url:** url for the action on rating
   */
  ratingAction: _propTypes.default.shape({
    label: _propTypes.default.string,
    url: _propTypes.default.string
  }),
  /**
   * Number of total reviews
   */
  reviewsCount: _propTypes.default.number,
  /**
   * Holds the action for QandA
   * - **label:** Label for the action on QandA
   * - **url:** url for the action on QandA
   */
  qAndAAction: _propTypes.default.shape({
    label: _propTypes.default.string,
    url: _propTypes.default.string
  }),
  /** Flag that indicates if a "write a review" link show display */
  reviewable: _propTypes.default.bool,
  /**
   * Text and link to indicate there are no reviews
   * - **label:** Label for the action on reviews
   * - **url:** url for the action on reviews
   */
  writeReviewAction: _propTypes.default.shape({
    label: _propTypes.default.string,
    url: _propTypes.default.string
  }),
  /**
   * Sets the review link from an external source that contains business logic
   */
  externalReviewLink: _propTypes.default.object,
  /**
   * Sets the Q&A link from an external source that contains business logic
   */
  externalQandALink: _propTypes.default.object,
  /**
   * Sets the write a review link from an external source that contains business logic
   */
  externalWriteReviewLink: _propTypes.default.object
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {number} maxRating - maxRating: 5 - sets the default for maximum stars to display
 * @property {number} rating - rating: 0 - sets the default rating value
 * @property {string} variant - variant: 'default'- sets the default variant of Ratings
 * @property {boolean} reviewable - reviewable: false - sets the default reviewable flag
 */

const defaultProps = exports.defaultProps = {
  maxRating: 5,
  rating: 0,
  variant: 'default',
  reviewable: false
};
var _default = exports.default = Ratings;